import {
  Create,
  getValueFromEvent,
  useFileUploadState,
  useForm,
  useStepsForm,
  useSelect,
} from "@refinedev/antd";
import { ReloadOutlined, UnorderedListOutlined, CloudServerOutlined, CheckCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Select,
  Steps,
  Tag,
  Alert,
  Result,
  Space,
} from "antd";

import {
  IResourceComponentsProps,
  useApiUrl,
  useNotification,
  useNavigation,
  useCustom,
} from "@refinedev/core";

import { IVesselStartupKit, IVessel } from "interfaces";
import { FormSection, FormFieldGrid } from "components/form";
import React from "react";
import { TOKEN_KEY } from "authProvider";

export const VesselStartupKitCreate: React.FC<
  IResourceComponentsProps
> = () => {
  const [isRefreshed, setIsRefreshed] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const { list } = useNavigation();
  const { open } = useNotification();

  const {
    current,
    gotoStep,
    stepsProps,
    formProps,
    formLoading: isLoading,
  } = useStepsForm<IVesselStartupKit>({
    resource: "vessel-startup-kit",
    redirect: false
  });

  const apiUrl = useApiUrl();

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const getSelectedVesselName = () => {
    const imoNumber = formProps.form?.getFieldValue(['vessel', 'imoNumber']);
    if (!imoNumber) return '';

    const vessels = vesselSelectProps.options || [];
    const vessel = vessels.find((v: any) => v.value === imoNumber);
    return vessel?.label || '';
  };

  const handleRefresh = async () => {
    setIsRefreshed(false);
    const imoNumber = formProps.form?.getFieldValue(['vessel', 'imoNumber']);

    if (!imoNumber) {
      open?.({
        type: "error",
        message: "Please select a vessel first",
      });
      return;
    }

    setRefreshing(true);

    try {
      const response = await fetch(`${apiUrl}/scheduler/vessel-startup-kit/update/${imoNumber}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });

      if (response.ok) {
        setIsRefreshed(true);
        open?.({
          type: "success",
          message: "The refresh request has been initiated. Kindly check back in 10 minutes for the updated data.",
        });
      } else {
        throw new Error('Failed to refresh vessel data');
      }
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to refresh vessel data. Please try again.",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const formList = [
    <FormSection
      title="Vessel Selection"
      subtitle="Select vessel for startup kit refresh"
      icon={<CloudServerOutlined />}
      variant="elevated"
    >
      <FormFieldGrid columns={1}>
        <Form.Item
          label="Vessel"
          name={["vessel", "imoNumber"]}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select {...vesselSelectProps} />
        </Form.Item>
      </FormFieldGrid>
    </FormSection>,
    <FormSection
      title="Data Refresh"
      subtitle="Trigger vessel startup kit data refresh"
      icon={<CheckCircleOutlined />}
      variant="elevated"
    >
      <Alert
        message="Data Refresh Confirmation"
        description={`You are about to request a data refresh for ${getSelectedVesselName()}'s startup kit. Once triggered, check back in 10 minutes for the updated data.`}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {isRefreshed ? (
        <Result
          status="success"
          title="Refresh Requested Successfully"
          subTitle={<strong>Kindly check back in 10 minutes for the updated data.</strong>}
        />
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <Button
            type="primary"
            onClick={handleRefresh}
            loading={refreshing}
            icon={<ReloadOutlined />}
            size="large"
          >
            Trigger Data Refresh
          </Button>
        </div>
      )}
    </FormSection>,
  ];

  return (
    <Create
      breadcrumb={false}
      headerProps={{
        onBack: () => list("vessel-startup-kit"),
        title: "Vessel Startup Kit",
        subTitle: "refresh vessel data",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (<></>),
      }}
      saveButtonProps={{
        children: "Go to List",
        icon: <UnorderedListOutlined />,
        onClick: () => list("vessel-startup-kit"),
        style: { display: isRefreshed ? 'block' : 'none' }
      }}
    >
      <Steps {...stepsProps}>
        <Steps.Step title="Select Vessel" />
        <Steps.Step title="Confirm Data Refresh" />
      </Steps>
      <div>
        <Form {...formProps} layout="vertical" style={{ paddingTop: 20 }}>
          <Space direction="vertical" size={24} style={{ width: "100%" }}>
            {formList[current]}
          </Space>
        </Form>
        <>
          {current > 0 && (
            <div
              style={{
                paddingTop: 20,
              }}
            >
              <Button
                onClick={() => {
                  gotoStep(current - 1);
                }}
                disabled={refreshing}
              >
                Previous
              </Button>
            </div>
          )}
          {current < formList.length - 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => {
                  gotoStep(current + 1);
                }}
              >
                Next
              </Button>
            </div>
          )}
        </>
      </div>
    </Create>
  );
};
